import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PlanCard } from '@/components/Payment/PlanCard'
import { Skeleton } from '@/components/ui/skeleton'
import { PricingProduct } from '@/store/api/Stripe/Stripe.types'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

interface PlansGridProps {
  plans: PricingProduct[]
  onSelectPlan: (product: PricingProduct) => void
  isLoading?: boolean
}

export const PlansGrid = ({
  plans,
  onSelectPlan,
  isLoading,
}: PlansGridProps) => {
  const { t } = useTranslation()

  const commonFeatures = [
    t('payment.paywall.feature1'),
    t('payment.paywall.feature2'),
    t('payment.paywall.feature3'),
    t('payment.paywall.feature4'),
  ]

  if (isLoading) {
    return (
      <div className="flex flex-col gap-10 w-full max-w-7xl mx-auto px-6 py-10">
        <Skeleton className="h-[200px] w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Skeleton className="h-[450px] rounded-2xl" />
          <Skeleton className="h-[450px] rounded-2xl hidden md:block" />
          <Skeleton className="h-[450px] rounded-2xl hidden lg:block" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto lg:px-6 lg:py-8">
      <div className="bg-white rounded-2xl p-4 lg:p-8 border-2 border-neutralColor10">
        <h2 className="text-neutralColor80 text-2xl lg:text-[44px] font-bold mb-6 lg:mb-8 tracking-tight">
          {t('payment.paywall.includedTitle')}
        </h2>

        <div className="flex flex-col gap-4 lg:gap-5">
          {commonFeatures.map((feature, index) => (
            <div key={index} className="flex items-center gap-4">
              <span className="text-neutralColor80 shrink-0">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <p className="text-neutralColor60 text-base lg:text-[18px] font-medium leading-relaxed">
                {feature}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch pt-4">
        {plans.map((plan) => {
          const isMonthly = plan.type === 'monthly'

          return (
            <div key={plan.priceId} className="h-full">
              <PlanCard
                title={plan.name}
                currencySymbol={plan.formattedPrice
                  .replace(/[0-9.,\s\u00A0]+$/, '')
                  .trim()}
                price={plan.formattedPrice.replace(/^[^0-9.]+/, '').trim()}
                discount={plan.discount ? `${plan.discount}% OFF` : undefined}
                billing={
                  plan.isRecurring
                    ? plan.interval === 'year'
                      ? t('payment.paywall.plans.annual.billing')
                      : t('payment.paywall.plans.earlyAdopter.billing')
                    : t('payment.paywall.plans.oneTime.billing')
                }
                benefit={
                  plan.isRecurring
                    ? `${plan.credits} ${
                        plan.credits === 1 ? 'scan' : 'scans'
                      } / ${plan.interval}`
                    : `${plan.credits} ${
                        plan.credits === 1 ? 'scan' : 'scans'
                      } credit`
                }
                buttonText={
                  plan.isRecurring
                    ? plan.interval === 'year'
                      ? t('payment.paywall.plans.annual.button')
                      : t('payment.paywall.plans.earlyAdopter.button')
                    : t('payment.paywall.plans.oneTime.button')
                }
                isPrimary={isMonthly}
                onAction={() => onSelectPlan(plan)}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
